"use client";

import { TaxIncentiveStatuses } from "@/prisma-package/src";
import classNames from "classnames";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { updateLedgerEntryStatusAction } from "@/app/_lib/actions/ledger.actions";
import { useParams } from "next/navigation";
import { useAtom } from "jotai";
import { FetchStates, Maybe } from "@/app/_lib/types/utility.types";
import { ledgerCalculationAtom } from "@/app/_lib/atoms/ledger-calculation.atom";
import { recalculateLedgerTotalsOnStatusChange } from "@/app/_lib/utils/recalculate-ledger-totals-on-status-change.utils";

type Props = {
  entryId: string;
  status: TaxIncentiveStatuses;
  amount: string;
  taxIncentiveAmount: Maybe<number>;
};

const StatusSelect: FC<Props> = ({
  status,
  entryId,
  amount,
  taxIncentiveAmount,
}) => {
  const [selectStatus, setSelectStatus] = useState<FetchStates>("idle");
  const [value, setValue] = useState<TaxIncentiveStatuses>(status);
  const [ledgerCalculation, setLedgerCalculation] = useAtom(
    ledgerCalculationAtom
  );
  const { id, ledgerUploadId } = useParams();

  useEffect(() => {
    setValue(status);
  }, [status]);

  const handleChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value as TaxIncentiveStatuses;
    const previousStatus = value;

    setValue(newStatus);

    setSelectStatus("loading");
    (async () => {
      const [updateErr, updateRes] = await updateLedgerEntryStatusAction({
        projectId: id as string,
        ledgerUploadId: ledgerUploadId as string,
        entryId,
        status: newStatus,
      });
      if (updateErr) {
        setSelectStatus("error");
        console.error(updateErr.message);
        // Revert to previous status on error
        setValue(previousStatus);
      } else if (updateRes?.success) {
        setSelectStatus("success");
        // Update ledger calculation totals on successful status change
        if (ledgerCalculation.totals) {
          setLedgerCalculation({
            totals: recalculateLedgerTotalsOnStatusChange({
              currentTotals: ledgerCalculation.totals,
              amount,
              taxIncentiveAmount,
              previousStatus,
              newStatus,
            }),
            hasClientModifications: true,
          });
        }
      }
      setTimeout(() => {
        setSelectStatus("idle");
      }, 3000);
    })();
  };

  return (
    <label className="relative form-control w-full max-w-xs">
      <div className="label">
        <span className="label-text">Change Tax Incentive Status:</span>
      </div>
      <select
        title="Select Tax Incentive Status"
        className={classNames("select", {
          "!bg-success": value === TaxIncentiveStatuses.QUALIFIED,
          "!bg-warning": value === TaxIncentiveStatuses.PENDING_REVIEW,
          "!bg-error": value === TaxIncentiveStatuses.NOT_QUALIFIED,
          "!bg-info": value === TaxIncentiveStatuses.POTENTIALLY_QUALIFIED,
        })}
        value={value}
        onChange={handleChange}
      >
        {Object.values(TaxIncentiveStatuses).map((status, idx) => (
          <option key={`${status}_${idx}`}>{status}</option>
        ))}
      </select>
      {selectStatus !== "idle" && (
        <div className="absolute -bottom-8 label">
          <span
            className={classNames("label-text-alt", {
              "text-success": selectStatus === "success",
              "text-error": selectStatus === "error",
            })}
          >
            {(() => {
              switch (selectStatus) {
                case "loading":
                  return "Loading...";
                case "success":
                  return "Saved!";
                case "error":
                  return "There was an error saving the status.";
                default:
                  return null;
              }
            })()}
          </span>
        </div>
      )}
    </label>
  );
};

export default StatusSelect;
